import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Axios from "axios";
import { useSignOut } from "react-auth-kit";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { tokens } from "../../../theme";
import { getAuthHeader } from "../../../utils/authToken";
import Loading from "../../../components/Loading";
import EttReportView from "../../../adminDashboard/ettReports/EttReportView";

const ClassroomETTReportDetail = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const signOut = useSignOut();
    const navigate = useNavigate();
    const { id: courseId, ettUserId, testId } = useParams();
    const studentId = new URLSearchParams(window.location.search).get('studentId');

    const courseData = useOutletContext()?.courseData;
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);

    // Classroom view shows only the section breakdown for the course's subject (the score
    // summary still reflects the full report — matches prior behavior). EttReportView applies
    // this predicate to the section breakdown.
    const courseTitle = courseData?.course_title?.toUpperCase() || "";
    const subjectFilter = courseTitle.includes("MATH") ? "math"
        : courseTitle.includes("ENGLISH") ? "english"
        : null;
    const sectionFilter = (section) => {
        if (subjectFilter === "math") return section.module_num >= 4;
        if (subjectFilter === "english") return section.module_num <= 3;
        return true;
    };

    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    };

    useEffect(() => {
        localStorage.setItem('selectedMenu', 'ETT Reports');

        const fetchReport = async () => {
            try {
                const response = await Axios.get(
                    `${process.env.REACT_APP_URL}/api/v1/ett-reports/users/${ettUserId}/tests/${testId}/report`,
                    {
                        params: { courseId, studentId },
                        headers: { Authorization: getAuthHeader() },
                    }
                );
                setReport(response.data);
            } catch (error) {
                if (error.response?.status === 401) {
                    handleLogout();
                } else {
                    console.error("Fetch report error:", error);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ettUserId, testId]);

    if (loading) return <Loading />;

    if (!report) {
        return (
            <Box m="20px">
                <IconButton onClick={() => navigate(`/classrooms/${courseId}/ett-reports`)}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography color={colors.grey[300]} mt="20px">
                    Report not found.
                </Typography>
            </Box>
        );
    }

    return (
        <Box m="20px">
            {/* Action bar */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="20px">
                <Box display="flex" alignItems="center" gap="10px">
                    <IconButton onClick={() => navigate(`/classrooms/${courseId}/ett-reports`)}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" fontWeight="bold">
                        ETT Exam Report
                    </Typography>
                </Box>
            </Box>

            {/* Shared printable report body, narrowed to this course's subject. */}
            <EttReportView report={report} sectionFilter={sectionFilter} />
        </Box>
    );
};

export default ClassroomETTReportDetail;
